import React, { useEffect } from 'react'
import styled from 'styled-components'
import { rem, transparentize } from 'polished'
import { useIntl, FormattedMessage } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import PaletteSheet from 'LuxKit/PaletteSheet'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import NotAvailablePlaceholder from 'loyalty/components/NotAvailablePlaceholder'
import checkPersonalisedSaving from 'luxPlus/lib/checkPersonalisedSaving'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchMembershipMetrics } from 'actions/LuxPlusActions'

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin: 0 auto;
`

const StatCardWrapper = styled(PaletteSheet)`
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: 0 ${rem(17)} ${rem(29)} ${transparentize(0.7, '#000000')};
  overflow: hidden;
  width: ${rem(250)};

  ${mediaQueryUp.tablet} {
    width: ${rem(280)};
  }
`

const StatCardWrapperBlue = styled(StatCardWrapper)`
  border: 1px solid ${props => props.theme.palette.highlight.primary.lightForeground};
  position: relative;
  z-index: 1;
  max-width: ${rem(204)};
  margin-top: ${rem(-20)};
  margin-left: ${rem(100)};

  ${mediaQueryUp.tablet} {
    width: ${rem(184)};
    margin-left: ${rem(120)};
  }

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(170)};
  }
`

const StatCardContentWhite = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(40)} ${rem(20)};
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  align-items: center;
`

const StatCardContentBlue = styled.div`
  background: ${props => props.theme.palette.product.luxPlus.background};
  padding: ${rem(40)} ${rem(20)};
  text-align: center;
`

const ClickableUnderline = styled.button`
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
`

interface Props {
  saveAmount: number
  firstName?: string
  personalSaving?: number
  onPastBookingsClick?: () => void
}

function LuxPlusStatCards({ saveAmount, firstName, personalSaving, onPastBookingsClick }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const saving = checkPersonalisedSaving(firstName, personalSaving)

  const membershipMetrics = useAppSelector(state => state.luxPlus.membershipMetrics)
  const trustedByCount = membershipMetrics.data?.trustedByCount
  const isTrustedByLoading = (membershipMetrics.initial || membershipMetrics.fetching)
  const isAustralianRegion = useAppSelector(state => state.geo.currentRegionCode === 'AU')
  useEffect(() => {
    dispatch(fetchMembershipMetrics())
  }, [dispatch])

  const couldHaveSavedLabel = intl.formatMessage(
    {
      id: 'mIL_2zZ9moPwGAuwgsCG',
      defaultMessage: '{firstName}, you could have',
      description: 'Personalised heading telling the user they could have saved with LuxPlus+',
    },
    { firstName },
  )

  const withProgramNameLabel = intl.formatMessage(
    {
      id: 'PcPeHmWN1g3MJCFV6fDp',
      defaultMessage: 'with {programName}',
      description: 'Suffix indicating savings are with the LuxPlus+ program',
    },
    { programName: LUXURY_PLUS.PROGRAM_NAME },
  )

  const mostMembersLabel = intl.formatMessage(
    {
      id: 'gh9E_WiixK76NMYpEsQ8',
      defaultMessage: 'Most {programName} members',
      description: 'Introduction text for general LuxPlus+ savings statistic',
    },
    { programName: LUXURY_PLUS.PROGRAM_NAME },
  )

  const eachYearLabel = intl.formatMessage({
    id: 'cIBX1WP_EzmpL7otC3_e',
    defaultMessage: 'each year*',
    description: 'Suffix for annual savings amount with asterisk disclaimer',
  })

  const trustedByLabel = intl.formatMessage({
    id: 'Qov_WuLVbN3WxPI_AimG',
    defaultMessage: 'Trusted by more than',
    description: 'Heading text for the traveller count statistic card on LuxPlus page',
  })

  const travellersLabel = isAustralianRegion
    ? intl.formatMessage({
        id: 'NPAsU_SnHiMOWEeqc_aN',
        defaultMessage: 'Australians',
        description: 'Label below the traveller count statistic for Australian users',
      })
    : intl.formatMessage({
        id: '2Z-PMociLhzvJMlsExsB',
        defaultMessage: 'travellers',
        description: 'Label below the traveller count statistic for non-AU users',
      })

  return (
    <CardsContainer>
      <StatCardWrapper paletteType="default">
        <StatCardContentWhite>
          {saving.showPersonalised && <>
            <BodyText variant="large" weight="bold" colour="neutral-two">
              {couldHaveSavedLabel}
            </BodyText>
            <Heading variant="heading3" colour="highlight-primary-dark">
              <FormattedMessage
                id="UfATKFH8b4GhjwYV5p0x"
                defaultMessage="saved at least {amount}"
                description="Personal savings amount the user could have saved on past bookings"
                values={{
                  amount: <FormatCurrency value={saving.personalSaving} format="rounded" />,
                }}
              />
            </Heading>
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              <FormattedMessage
                id="NYGTprgC3hF9v0y0fjW1"
                defaultMessage="on <pastBookings>past bookings</pastBookings>"
                description="Link to view past bookings where savings could have applied"
                values={{
                  pastBookings: (chunks: React.ReactNode) => <ClickableUnderline type="button" onClick={onPastBookingsClick}>{chunks}</ClickableUnderline>,
                }}
              />
            </BodyText>
            <BodyText variant="large" weight="bold" colour="neutral-two">
              {withProgramNameLabel}
            </BodyText>
          </>}
          {!saving.showPersonalised && <>
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              {mostMembersLabel}
            </BodyText>
            <Heading variant="heading3" colour="highlight-primary-dark">
              <FormattedMessage
                id="SPvRRh7gRfsH5mZN-6SV"
                defaultMessage="save over {amount}"
                description="General savings amount most LuxPlus+ members save each year"
                values={{
                  amount: <FormatCurrency value={saveAmount} format="rounded" />,
                }}
              />
            </Heading>
            <Heading variant="heading3" colour="highlight-primary-dark">{eachYearLabel}</Heading>
          </>}
        </StatCardContentWhite>
      </StatCardWrapper>

      <StatCardWrapperBlue paletteType="inverse">
        <StatCardContentBlue>
          <BodyText weight="bold" variant="medium" colour="neutral-one">{trustedByLabel}</BodyText>
          <Heading variant="heading3" colour="highlight-primary-dark">
            {isTrustedByLoading && <TextLoadingBox width="7ch" typography="heading3" style={{ opacity: 0.3 }} />}
            {!isTrustedByLoading && trustedByCount !== undefined && <FormatDecimal value={trustedByCount} /> }
            {!isTrustedByLoading && trustedByCount === undefined && <NotAvailablePlaceholder />}
          </Heading>
          <Heading variant="heading3" colour="highlight-primary-dark">{travellersLabel}</Heading>
        </StatCardContentBlue>
      </StatCardWrapperBlue>
    </CardsContainer>
  )
}

export default LuxPlusStatCards
